import { useCallback, useEffect, useState } from "react";

import ThemeProvider from "react-bootstrap/ThemeProvider";
import { IconContext } from "react-icons";

import { ModalProvider } from "@evos/components";
import { useKeycloak } from "@react-keycloak/web";
import { authProvider, AuthContext } from "lib/context/auth";
import type { AuthProps } from "lib/context/auth";

interface Children {
  children: React.ReactNode
}

function AuthProvider({ children }: Children ) {
  const { keycloak, initialized } = useKeycloak();

  const [ user, setUser ] = useState<AuthProps["user"] | null>(null);
  const [ expiredTokenError, setExpiredTokenError ] = useState<boolean>(false);
  const [ unknownUserError, setUnknownUserError ] = useState<boolean>(false);
  const [ isIdle, setIsIdle ] = useState<boolean>(false);
  const [ darkMode, setDarkMode ] = useState<boolean>(false);

  const persistUser = useCallback((nextUser: AuthProps["user"] | null) => {
    if (nextUser != null) {
      localStorage.setItem("monitoring_user", JSON.stringify(nextUser));
      if (nextUser.token != null) {
        localStorage.setItem("monitoring_user_token", nextUser.token);
      }
    } else {
      localStorage.removeItem("monitoring_user");
      localStorage.removeItem("monitoring_user_token");
    }
    setUser(nextUser);
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("monitoring_user");
    if (storedUser != null) {
      try {
        const parsedUser = JSON.parse(storedUser) as AuthProps["user"];
        const storedToken = localStorage.getItem("monitoring_user_token");
        if (parsedUser != null) {
          const sanitizedUser: AuthProps["user"] = {
            given_name: parsedUser.given_name ?? "",
            email: parsedUser.email ?? "",
            sub: parsedUser.sub ?? "",
            roles: Array.isArray(parsedUser.roles) ? parsedUser.roles : [],
            token: storedToken ?? parsedUser.token ?? undefined,
          };
          setUser(sanitizedUser);
        } else {
          setUser(null);
        }
      } catch (_error) {
        localStorage.removeItem("monitoring_user");
        localStorage.removeItem("monitoring_user_token");
      }
    }

    const storedTheme = localStorage.getItem("colorScheme");
    if (storedTheme === "dark") {
      setDarkMode(true);
    }
  }, []);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    const tokenParsed = keycloak?.tokenParsed;

    if (keycloak?.authenticated === true && tokenParsed != null) {
      const roles = (tokenParsed.realm_access?.roles ?? []) as string[];
      const mappedUser = {
        given_name: tokenParsed.given_name ?? tokenParsed.name ?? tokenParsed.preferred_username ?? "",
        email: tokenParsed.email ?? "",
        sub: tokenParsed.sub ?? "",
        roles,
        token: keycloak.token ?? undefined,
      };
      authProvider.signin(() => {
        persistUser(mappedUser);
        setExpiredTokenError(false);
      });
    } else if (keycloak?.authenticated === false) {
      persistUser(null);
    }
  }, [initialized, keycloak, persistUser]);

  useEffect(() => {
    if (!initialized || keycloak == null) {
      return;
    }

    keycloak.onTokenExpired = () => {
      keycloak
        .updateToken(60)
        .then((refreshed) => {
          if (refreshed === true && keycloak.token != null) {
            setUser((current) => current == null ? current : ({ ...current, token: keycloak.token ?? undefined }));
          } else {
            setExpiredTokenError(true);
          }
        })
        .catch(() => setExpiredTokenError(true));
    };

    return () => {
      keycloak.onTokenExpired = undefined;
    };
  }, [initialized, keycloak]);

  const signin = (newUser: AuthProps["user"], callback: () => void) => {
    authProvider.signin(() => {
      persistUser(newUser);
      callback();
    });
  };

  const signout = (callback: () => void) => {
    const cleanup = () => {
      persistUser(null);
      setExpiredTokenError(false);
      authProvider.signout(callback);
    };

    keycloak
      ?.logout({ redirectUri: `${window.location.origin}/login` })
      .then(cleanup)
      .catch((error) => {
        console.error("Keycloak logout failed", error);
        cleanup();
      });
  };

  const value = {
    user,
    signin,
    signout,
    darkMode,
    setDarkMode,
    isIdle,
    setIsIdle,
    expiredTokenError,
    setExpiredTokenError,
    unknownUserError,
    setUnknownUserError
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default function Providers({ children }: Children) {
  return (
    <AuthProvider>
      <ThemeProvider
        breakpoints={["six-xl", "five-xl", "four-xl", "xxxl", "xxl", "xl", "lg", "md", "sm", "xs", "xs"]}
        minBreakpoint="xs"
      >
        <IconContext.Provider value={{ style: { verticalAlign: "text-bottom", marginBottom: "1px" } }}>
          <ModalProvider>
            {children}
          </ModalProvider>
        </IconContext.Provider>
      </ThemeProvider>
    </AuthProvider>
  );
}
